import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';

import { cn } from '../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Skeleton } from './ui/skeleton';

export interface SearchSelectOption {
  value: string;
  label: string;
  /** Muted second line, e.g. an email or a client name — truncated, never wrapped. */
  description?: string;
}

export interface SearchSelectProps {
  id?: string;
  value: string | null;
  onValueChange: (value: string) => void;
  /** The typed search text, debounced here so every caller gets the same behaviour without repeating it. */
  onSearchChange: (search: string) => void;
  options: SearchSelectOption[];
  /** The selected option's label, resolved by the caller when it falls outside the current page of `options`. */
  selectedLabel?: string | null;
  isLoading?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  invalid?: boolean;
  /** The control's name when no visible label points at it through `id`; a placeholder is not a name. */
  ariaLabel?: string;
}

const SEARCH_DEBOUNCE_MS = 250;

/**
 * A field's label sits directly above its trigger with only a small gap (see
 * `Field`, `packages/ui/src/components/field.tsx`), so a popover Radix flips
 * above the trigger for lack of room below would cover it. The flipped panel
 * is lifted clear with a CSS translate keyed on Radix's own `data-side`: a
 * transform does not change the size Radix measures, so the flip decision and
 * the clearance cannot feed back into each other. (A side offset held in
 * state did. The lift is the label's height and gap plus 4px: lifting by
 * exactly that left the panel touching the label, and sub-pixel scroll
 * positions made it overlap by a fraction of a pixel about half the time.)
 */
const FLIPPED_CLEARANCE_CLASS = 'data-[side=top]:-translate-y-7';

/**
 * A server-filtered combobox with no data fetching of its own: the caller
 * owns the query — people, clients, projects, whatever it lists — and hands
 * back `options` and `isLoading`; this owns the popover sizing, the search
 * debounce and the keyboard/aria wiring, so every searchable list in the app
 * opens, scrolls and behaves the same way.
 */
export function SearchSelect({
  id,
  value,
  onValueChange,
  onSearchChange,
  options,
  selectedLabel,
  isLoading = false,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches',
  invalid = false,
  ariaLabel,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = useId();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // A JSX `autoFocus` attribute steals focus even when a page merely mounts;
  // moving it by hand, only in response to the popover actually opening, is
  // also what keeps typing working the instant it does.
  useEffect(() => {
    if (open) searchInputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => onSearchChange(search), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search, onSearchChange]);

  function selectOption(optionValue: string): void {
    onValueChange(optionValue);
    setOpen(false);
    setSearch('');
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, Math.max(options.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = options[activeIndex];
      if (option) selectOption(option.value);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-invalid={invalid}
          aria-label={ariaLabel}
          className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-3 text-sm shadow-xs outline-none transition-[color,box-shadow,border-color,background-color] duration-150 ease-out hover:border-ring/40 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-invalid:border-destructive aria-invalid:ring-destructive/20"
        >
          {value && selectedLabel ? (
            <span className="truncate text-left font-medium">{selectedLabel}</span>
          ) : (
            <span className="truncate text-placeholder">{placeholder}</span>
          )}
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>

      {/*
       * Sized from the trigger's own width, never a fixed one, so the panel
       * never spills into a neighbouring field on a narrow column; capped at
       * ~288px with the list scrolling beneath a header that never moves, so
       * opening it never grows the page. `avoidCollisions` flips it above the
       * trigger — never over it — when the field sits near the bottom of the
       * viewport.
       */}
      <PopoverContent
        side="bottom"
        align="start"
        avoidCollisions
        className={`flex max-h-72 w-(--radix-popover-trigger-width) min-w-(--radix-popover-trigger-width) max-w-(--radix-popover-trigger-width) flex-col overflow-hidden p-0 ${FLIPPED_CLEARANCE_CLASS}`}
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            ref={searchInputRef}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-activedescendant={
              options[activeIndex] ? `${listId}-${options[activeIndex].value}` : undefined
            }
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>

        <div
          id={listId}
          role="listbox"
          aria-label={placeholder}
          className="min-h-0 flex-1 overflow-y-auto p-1"
        >
          {isLoading ? (
            <div className="flex flex-col gap-1 p-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : options.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyText}</div>
          ) : (
            options.map((option, index) => (
              <div
                key={option.value}
                id={`${listId}-${option.value}`}
                role="option"
                tabIndex={-1}
                aria-selected={option.value === value}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectOption(option.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  selectOption(option.value);
                }}
                className={cn(
                  'flex cursor-pointer flex-col gap-0.5 rounded-sm px-2 py-1.5 text-sm select-none',
                  index === activeIndex && 'bg-accent text-accent-foreground',
                )}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">{option.label}</span>
                  {option.value === value ? (
                    <Check className="size-4 shrink-0 text-primary" />
                  ) : null}
                </span>
                {option.description ? (
                  <span className="truncate text-xs text-muted-foreground">
                    {option.description}
                  </span>
                ) : null}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
